import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControlLabel,
  MenuItem,
  Select,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import CasinoRoundedIcon from '@mui/icons-material/CasinoRounded'
import StarRoundedIcon from '@mui/icons-material/StarRounded'
import ContentContainer from '../../areas/layout/content-container'
import discoverApi from '../../client/discover-api'
import searchApi from '../../client/search-api'
import { useMediaState } from '../../context/media-state-context'
import { DiscoverItem, Mood } from '../../types/Discover'
import {
  StyledFiltersRow,
  StyledImportButton,
  StyledMoodAddChip,
  StyledMoodRow,
  StyledPage,
  StyledPoolGrid,
  StyledPoolItem,
  StyledPoolPoster,
  StyledPoolPosterFallback,
  StyledRemoveBadge,
  StyledSearchDropdown,
  StyledSearchInput,
  StyledSearchRow,
  StyledSearchThumb,
  StyledSearchWrapper,
  StyledSpinArea,
  StyledSpinButton,
  StyledSpotlight,
  StyledSpotlightImage,
  StyledSubtitle,
  StyledTitle,
  StyledToolsRow,
  StyledWinnerMeta,
} from './randomizer-styles'
import { ReusableTypes } from '../../types/ReusableTypes'

type PoolItem = {
  id: number
  type: 'movie' | 'show'
  title: string
  posterUrl?: string
  year?: string
  rating?: number
  genres?: string[]
}

type SearchHit = {
  id: number
  type: ReusableTypes['MovieShowPerson']
  title: string
  posterUrl?: string
  year?: string
  rating?: number
  genres?: string[]
}

const POOL_STORAGE_KEY = 'movieception_randomizer_pool'

const poolKey = (item: { type: string; id: number }) =>
  `${item.type}-${item.id}`

const loadStoredPool = (): PoolItem[] => {
  try {
    const raw = localStorage.getItem(POOL_STORAGE_KEY)
    return raw ? (JSON.parse(raw) as PoolItem[]) : []
  } catch {
    return []
  }
}

// Deceleration profile for the reel: fast flips easing into the reveal.
const SPIN_DELAYS = [
  70, 70, 70, 70, 70, 80, 80, 90, 90, 100, 110, 120, 140, 160, 190, 230, 280,
  340, 420, 520,
]

const RandomizerPage = () => {
  const { mediaState, historySet, isAuthenticated } = useMediaState()

  const [pool, setPool] = useState<PoolItem[]>(loadStoredPool)
  const [moods, setMoods] = useState<Mood[]>([])
  const [addingMood, setAddingMood] = useState<string | null>(null)

  const [searchQuery, setSearchQuery] = useState('')
  const [searchHits, setSearchHits] = useState<SearchHit[]>([])
  const [searchOpen, setSearchOpen] = useState(false)

  const [mediaFilter, setMediaFilter] = useState<'all' | 'movie' | 'show'>(
    'all',
  )
  const [unwatchedOnly, setUnwatchedOnly] = useState(false)
  const [minRating, setMinRating] = useState(0)

  const [phase, setPhase] = useState<'idle' | 'spinning' | 'revealed'>('idle')
  const [spotlight, setSpotlight] = useState<PoolItem | null>(null)
  const [winner, setWinner] = useState<PoolItem | null>(null)
  const [vetoUsed, setVetoUsed] = useState(false)
  const [vetoedKey, setVetoedKey] = useState<string | null>(null)
  const spinTimer = useRef<ReturnType<typeof setTimeout> | null>(null)

  useEffect(() => {
    discoverApi
      .getMoods()
      .then(setMoods)
      .catch(() => setMoods([]))
    return () => {
      if (spinTimer.current) clearTimeout(spinTimer.current)
    }
  }, [])

  useEffect(() => {
    try {
      localStorage.setItem(POOL_STORAGE_KEY, JSON.stringify(pool))
    } catch {
      /* storage full or blocked; the pool just won't survive a refresh */
    }
  }, [pool])

  // Debounced search against the existing /search endpoint; people are not
  // spinnable, so they never make the dropdown.
  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchHits([])
      return
    }
    const handle = setTimeout(async () => {
      try {
        const res = await searchApi.search(searchQuery.trim())
        const hits = (res.data.results as SearchHit[])
          .filter((r) => r.type === 'movie' || r.type === 'show')
          .slice(0, 6)
        setSearchHits(hits)
        setSearchOpen(true)
      } catch {
        setSearchHits([])
      }
    }, 350)
    return () => clearTimeout(handle)
  }, [searchQuery])

  const addItems = useCallback((items: PoolItem[]) => {
    setPool((prev) => {
      const existing = new Set(prev.map(poolKey))
      const fresh = items.filter((item) => !existing.has(poolKey(item)))
      return fresh.length > 0 ? [...prev, ...fresh] : prev
    })
  }, [])

  const importFromList = (source: 'watchlist' | 'favorites') => {
    const list =
      source === 'watchlist' ? mediaState?.watchlist : mediaState?.favorites
    const items: PoolItem[] = (list ?? [])
      .filter((item) => {
        const type = item.mediaType.toLowerCase()
        return type === 'movie' || type === 'show'
      })
      .map((item) => ({
        id: item.tmdbId,
        type:
          item.mediaType.toLowerCase() === 'movie'
            ? ('movie' as const)
            : ('show' as const),
        title: item.title,
        posterUrl: item.posterPath ?? undefined,
        year: item.releaseYear ? String(item.releaseYear) : undefined,
        genres: item.genres,
      }))
    addItems(items)
  }

  const addFromMood = async (mood: Mood) => {
    setAddingMood(mood.key)
    try {
      const results = await discoverApi.getMoodResults(mood.key, 12)
      addItems(
        results.slice(0, 8).map((item: DiscoverItem) => ({
          id: item.id,
          type: item.type,
          title: item.title,
          posterUrl: item.posterUrl,
          year: item.year,
          rating: item.rating,
          genres: item.genres,
        })),
      )
    } catch {
      /* a failed mood add just adds nothing */
    } finally {
      setAddingMood(null)
    }
  }

  const addSearchHit = (hit: SearchHit) => {
    if (hit.type === 'person') return
    addItems([
      {
        id: hit.id,
        type: hit.type,
        title: hit.title,
        posterUrl: hit.posterUrl,
        year: hit.year,
        rating: hit.rating,
        genres: hit.genres,
      },
    ])
    setSearchQuery('')
    setSearchHits([])
    setSearchOpen(false)
  }

  const removeItem = (key: string) => {
    setPool((prev) => prev.filter((item) => poolKey(item) !== key))
  }

  const eligible = useMemo(
    () =>
      pool.filter((item) => {
        if (mediaFilter !== 'all' && item.type !== mediaFilter) return false
        if (unwatchedOnly && historySet.has(item.id)) return false
        // Unknown ratings pass: don't punish watchlist imports for missing data.
        if (
          minRating > 0 &&
          item.rating !== undefined &&
          item.rating < minRating
        )
          return false
        return true
      }),
    [pool, mediaFilter, unwatchedOnly, historySet, minRating],
  )

  const spin = (excludeKey: string | null) => {
    const candidates = eligible.filter((item) => poolKey(item) !== excludeKey)
    if (candidates.length === 0) return

    const finalPick = candidates[Math.floor(Math.random() * candidates.length)]
    setPhase('spinning')
    setWinner(null)

    // Flip through shuffled candidates with growing delays, land on the pick.
    const reel = [...candidates].sort(() => Math.random() - 0.5)
    let step = 0
    const tick = () => {
      if (step < SPIN_DELAYS.length) {
        setSpotlight(reel[step % reel.length])
        spinTimer.current = setTimeout(tick, SPIN_DELAYS[step])
        step += 1
      } else {
        setSpotlight(finalPick)
        setWinner(finalPick)
        setPhase('revealed')
      }
    }
    tick()
  }

  const handleVeto = () => {
    if (!winner || vetoUsed) return
    setVetoUsed(true)
    setVetoedKey(poolKey(winner))
    spin(poolKey(winner))
  }

  const resetSpin = () => {
    setPhase('idle')
    setWinner(null)
    setSpotlight(null)
    setVetoUsed(false)
    setVetoedKey(null)
  }

  const spinDisabled =
    phase === 'spinning' ||
    eligible.filter((i) => poolKey(i) !== vetoedKey).length < 2

  return (
    <ContentContainer>
      <StyledPage>
        <StyledTitle variant='h4'>Randomizer</StyledTitle>
        <StyledSubtitle>
          Build your pool, spin the wheel. The second spin is final. No
          take-backs.
        </StyledSubtitle>

        <StyledToolsRow>
          {isAuthenticated && (
            <>
              <StyledImportButton onClick={() => importFromList('watchlist')}>
                + Add my Watchlist
              </StyledImportButton>
              <StyledImportButton onClick={() => importFromList('favorites')}>
                + Add my Favorites
              </StyledImportButton>
            </>
          )}
          <StyledSearchWrapper>
            <StyledSearchInput
              size='small'
              placeholder='Search anything to add...'
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onFocus={() => searchHits.length > 0 && setSearchOpen(true)}
              onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
            />
            {searchOpen && searchHits.length > 0 && (
              <StyledSearchDropdown>
                {searchHits.map((hit) => (
                  <StyledSearchRow
                    key={`${hit.type}-${hit.id}`}
                    onMouseDown={() => addSearchHit(hit)}
                  >
                    {hit.posterUrl ? (
                      <StyledSearchThumb src={hit.posterUrl} alt='' />
                    ) : (
                      <StyledSearchThumb as='div' />
                    )}
                    <Box>
                      <Typography variant='body2'>{hit.title}</Typography>
                      <Typography variant='caption' color='text.secondary'>
                        {hit.type === 'movie' ? 'Movie' : 'Show'}
                        {hit.year ? ` (${hit.year})` : ''}
                      </Typography>
                    </Box>
                  </StyledSearchRow>
                ))}
              </StyledSearchDropdown>
            )}
          </StyledSearchWrapper>
        </StyledToolsRow>

        {moods.length > 0 && (
          <StyledMoodRow>
            {moods.map((mood) => (
              <StyledMoodAddChip
                key={mood.key}
                onClick={() => addFromMood(mood)}
                disabled={addingMood !== null}
                title={`Add a few "${mood.label}" picks to the pool`}
              >
                {addingMood === mood.key ? (
                  <CircularProgress size={14} />
                ) : (
                  <span aria-hidden>{mood.emoji}</span>
                )}
                + {mood.label}
              </StyledMoodAddChip>
            ))}
          </StyledMoodRow>
        )}

        <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 2, mb: 1 }}>
          <Typography variant='h6'>The pool ({pool.length})</Typography>
          {pool.length > 0 && (
            <Button
              size='small'
              onClick={() => {
                setPool([])
                resetSpin()
              }}
            >
              Clear all
            </Button>
          )}
        </Box>

        <StyledPoolGrid>
          {pool.length === 0 && (
            <Typography color='text.secondary' sx={{ py: 4 }}>
              Nothing here yet. Add your watchlist, search for titles, or grab a
              mood above.
            </Typography>
          )}
          {pool.map((item) => {
            const key = poolKey(item)
            return (
              <StyledPoolItem key={key} title={item.title}>
                {item.posterUrl ? (
                  <StyledPoolPoster src={item.posterUrl} alt={item.title} />
                ) : (
                  <StyledPoolPosterFallback>
                    {item.title.slice(0, 1).toUpperCase()}
                  </StyledPoolPosterFallback>
                )}
                <StyledRemoveBadge
                  onClick={() => removeItem(key)}
                  aria-label={`Remove ${item.title}`}
                >
                  ✕
                </StyledRemoveBadge>
              </StyledPoolItem>
            )
          })}
        </StyledPoolGrid>

        {pool.length > 0 && (
          <StyledFiltersRow>
            <ToggleButtonGroup
              size='small'
              exclusive
              value={mediaFilter}
              onChange={(_, value) => value && setMediaFilter(value)}
            >
              <ToggleButton value='all'>All</ToggleButton>
              <ToggleButton value='movie'>Movies</ToggleButton>
              <ToggleButton value='show'>Shows</ToggleButton>
            </ToggleButtonGroup>

            {isAuthenticated && (
              <FormControlLabel
                control={
                  <Switch
                    size='small'
                    checked={unwatchedOnly}
                    onChange={(e) => setUnwatchedOnly(e.target.checked)}
                  />
                }
                label="Haven't watched"
              />
            )}

            <Select
              size='small'
              value={minRating}
              onChange={(e) => setMinRating(Number(e.target.value))}
            >
              <MenuItem value={0}>Any rating</MenuItem>
              <MenuItem value={6}>6+ only</MenuItem>
              <MenuItem value={7}>7+ only</MenuItem>
              <MenuItem value={8}>8+ only</MenuItem>
            </Select>

            <Typography variant='body2' color='text.secondary'>
              {eligible.length} of {pool.length} eligible
            </Typography>
          </StyledFiltersRow>
        )}

        {pool.length > 0 && (
          <StyledSpinArea>
            <StyledSpotlight>
              {spotlight?.posterUrl ? (
                <StyledSpotlightImage
                  src={spotlight.posterUrl}
                  alt={spotlight.title}
                />
              ) : spotlight ? (
                <Typography variant='h5' sx={{ px: 2, textAlign: 'center' }}>
                  {spotlight.title}
                </Typography>
              ) : (
                <CasinoRoundedIcon sx={{ fontSize: 64, opacity: 0.3 }} />
              )}
            </StyledSpotlight>

            {phase === 'revealed' && winner && (
              <StyledWinnerMeta>
                <Typography variant='h5' fontWeight={700}>
                  {winner.title}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {winner.rating !== undefined && (
                    <Chip
                      size='small'
                      icon={<StarRoundedIcon />}
                      label={winner.rating}
                    />
                  )}
                  {winner.year && (
                    <Typography variant='body2' color='text.secondary'>
                      {winner.year}
                    </Typography>
                  )}
                  {winner.genres?.slice(0, 3).map((genre) => (
                    <Chip
                      key={genre}
                      size='small'
                      variant='outlined'
                      label={genre}
                    />
                  ))}
                </Box>
                {vetoUsed && (
                  <Typography variant='body2' color='text.secondary'>
                    That's the one. You used your veto 🍿
                  </Typography>
                )}
              </StyledWinnerMeta>
            )}

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                flexWrap: 'wrap',
                justifyContent: 'center',
              }}
            >
              {phase !== 'revealed' && (
                <StyledSpinButton
                  onClick={() => spin(vetoedKey)}
                  disabled={spinDisabled}
                  startIcon={<CasinoRoundedIcon />}
                >
                  {phase === 'spinning' ? 'Spinning...' : 'Spin'}
                </StyledSpinButton>
              )}

              {phase === 'revealed' && winner && (
                <>
                  <StyledSpinButton
                    component={RouterLink}
                    to={`/details/${winner.type}/${winner.id}`}
                  >
                    Details
                  </StyledSpinButton>
                  {!vetoUsed && (
                    <Button onClick={handleVeto} sx={{ textTransform: 'none' }}>
                      Spin again (1 left)
                    </Button>
                  )}
                  <Button
                    onClick={resetSpin}
                    size='small'
                    sx={{ textTransform: 'none', color: 'text.secondary' }}
                  >
                    Start over
                  </Button>
                </>
              )}
            </Box>

            {pool.length > 0 && eligible.length < 2 && phase === 'idle' && (
              <Typography variant='body2' color='text.secondary'>
                Need at least 2 eligible titles to spin - add more or loosen the
                filters.
              </Typography>
            )}
          </StyledSpinArea>
        )}
      </StyledPage>
    </ContentContainer>
  )
}

export default RandomizerPage
